import { ShoppingBag, Tag, Edit, Trash, Copy, Loader2, ExternalLink, Plus, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { LazyImage } from '@/components/ui/lazy-image';
import { AspectRatio } from '@/components/ui/aspect-ratio';
import { useProductClicks } from '@/hooks/product/useProductClicks';

export interface Product {
  id: string;
  name: string;
  description?: string;
  price?: number;
  currency?: string;
  category?: string;
  image_url?: string;
  affiliate_link: string;
  created_at: string;
  source?: 'products' | 'recommendations';
  packing_list_id?: string;
  packing_list_title?: string;
}

interface ProductCardProps {
  product: Product;
  onEdit: (product: Product) => void;
  onDelete: (id: string, source: 'products' | 'recommendations') => void;
  onDuplicate?: (product: Product) => void;
  onAddToProducts?: (product: Product) => void;
  isDuplicating?: boolean;
  selectedProductId?: string;
  isSelectable?: boolean;
  isSelected?: boolean;
  onSelectChange?: (product: Product, isSelected: boolean) => void;
}

export function ProductCard({ 
  product, 
  onEdit, 
  onDelete, 
  onDuplicate, 
  onAddToProducts,
  isDuplicating,
  selectedProductId,
  isSelectable = false,
  isSelected = false,
  onSelectChange
}: ProductCardProps) {
  const { trackProductClick } = useProductClicks();
  
  const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    // Don't interfere with normal link behavior
    trackProductClick(product);
  };

  return (
    <Card 
      key={`${product.source}-${product.id}`} 
      className={`overflow-hidden relative ${isSelected ? 'ring-2 ring-primary' : ''}`}
      onClick={() => isSelectable && onSelectChange?.(product, !isSelected)}
    >
      {isSelectable && (
        <div className="absolute top-3 left-3 z-10">
          <Checkbox 
            checked={isSelected}
            onCheckedChange={(checked) => onSelectChange?.(product, !!checked)}
            className="h-5 w-5 bg-background/90 border-2"
          />
        </div>
      )}
      
      {product.image_url ? (
        <AspectRatio ratio={16/9} className="overflow-hidden">
          <LazyImage 
            src={product.image_url} 
            alt={product.name} 
            className="object-cover w-full h-full"
          />
          {product.source === 'recommendations' && (
            <div className="absolute top-2 right-2">
              <span className="bg-black/70 text-white text-xs px-2 py-1 rounded-md">
                Recommendation
              </span>
            </div>
          )}
        </AspectRatio>
      ) : (
        <AspectRatio ratio={16/9} className="bg-secondary flex items-center justify-center relative">
          <ShoppingBag className="h-12 w-12 text-muted-foreground/50" />
          {product.source === 'recommendations' && (
            <div className="absolute top-2 right-2">
              <span className="bg-black/70 text-white text-xs px-2 py-1 rounded-md">
                Recommendation
              </span>
            </div>
          )}
        </AspectRatio>
      )}
      
      <CardHeader className="p-4">
        <div className="flex justify-between">
          <div>
            <CardTitle className="text-lg">{product.name}</CardTitle>
            {product.category && (
              <div className="flex items-center mt-1 text-xs text-muted-foreground">
                <Tag className="h-3 w-3 mr-1" />
                {product.category}
              </div>
            )}
            {product.source === 'recommendations' && product.packing_list_title && (
              <div className="flex items-center mt-1 text-xs text-muted-foreground">
                <span>From: {product.packing_list_title}</span>
              </div>
            )}
          </div>
          {product.price && (
            <span className="text-sm font-semibold">
              ${product.price} {product.currency || 'USD'}
            </span>
          )}
        </div>
      </CardHeader>
      <CardContent className="p-4 pt-0">
        {product.description && (
          <p className="text-sm text-muted-foreground line-clamp-2 mb-2">
            {product.description}
          </p>
        )}
        <a 
          href={product.affiliate_link} 
          target="_blank" 
          rel="noopener noreferrer"
          className="text-xs text-blue-500 hover:underline line-clamp-1 flex items-center"
          onClick={handleLinkClick}
        >
          <span className="truncate">{product.affiliate_link}</span>
          <ExternalLink className="h-3 w-3 ml-1 flex-shrink-0" />
        </a>
      </CardContent>
      <CardFooter className="p-4 pt-0 flex justify-end gap-2">
        {isSelectable ? (
          <Button 
            variant={isSelected ? "default" : "outline"} 
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onSelectChange?.(product, !isSelected);
            }}
            className="text-xs mr-auto"
          >
            {isSelected ? (
              <>
                <Check className="h-3 w-3 mr-1" />
                Selected
              </>
            ) : (
              "Select"
            )}
          </Button>
        ) : (
          <>
            {product.source === 'recommendations' && onAddToProducts && (
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => onAddToProducts(product)}
                className="text-xs"
              >
                <Plus className="h-3 w-3 mr-1" />
                Add to Products
              </Button>
            )}
            {product.source === 'products' && onDuplicate && (
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => onDuplicate(product)}
                disabled={isDuplicating && selectedProductId === product.id}
                className="text-xs"
              >
                {isDuplicating && selectedProductId === product.id ? (
                  <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                ) : (
                  <Copy className="h-3 w-3 mr-1" />
                )}
                Duplicate
              </Button>
            )}
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => onEdit(product)}
            >
              <Edit className="h-4 w-4" />
            </Button>
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => onDelete(product.id, product.source)}
            >
              <Trash className="h-4 w-4" />
            </Button>
          </>
        )}
      </CardFooter>
    </Card>
  );
}
